import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild } from "@angular/core";
import { TableHeightService } from "../../services/table-height.service";
import { Subscription, debounceTime, take } from "rxjs";
import { NotificationDataService } from "./services/notification-data.service";
import { NotificationItemDto } from "./models/notification.models";
import { FilterModelDto } from "../../models/filter.model";
import { ModalService } from "../../services/modal.service";
import { NotificationService } from "../../services/notification.service";
import { UserInfoService } from "../../services/user-info.service";
import { FilterSettingService } from "src/app/services/filter-setting.service";

const NULL_PAGE = 0;
@Component({
	selector: "app-notifications",
	templateUrl: "./notifications.component.html",
	styleUrls: ["./notifications.component.scss"],
})
export class NotificationsComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild("tableBlock") tableBlock: ElementRef | undefined;
	@ViewChild("tableBody") tableBody: ElementRef | undefined;
	@ViewChild("notificationBlock") notificationBlock: ElementRef | undefined;

	filterStatuses: FilterModelDto[] | undefined;
	filterAdditional: FilterModelDto[] | undefined;
	priorityStatuses: FilterModelDto[] | undefined;
	relationStatuses: FilterModelDto[] | undefined;

	delta = 150;
	lastPage!: boolean;
	currentPage = 0;

	isOpenFilter = false;
	sortState = false;

	tabSelect: string | "all" | "for-me" | "common" = "all";

	subs: Subscription[] = [];

	notificationList: NotificationItemDto[] = [];

	selectedNotification: NotificationItemDto | null = null;

	isOpenDeleteNotification = false;
	isOpenDeleteAllNotification = false;

	notificationId: number | undefined;

	filterData: any;
	searchWord: string | undefined = "";

	constructor(
		private tableHeightService: TableHeightService,
		private renderer: Renderer2,
		private notificationDataService: NotificationDataService,
		private modalService: ModalService,
		private notificationService: NotificationService,
		private userInfoService: UserInfoService,
		private filterSettingService: FilterSettingService
	) {}

	ngOnInit() {
		this.initFilter();
		this.loadNotification();
		this.initModal();
	}

	ngAfterViewInit() {
		this.subs.push(
			this.tableHeightService.notificationsHeight$.subscribe({
				next: (res) => {
					this.renderer.setStyle(this.notificationBlock?.nativeElement, "height", res);
				},
			})
		);
	}

	ngOnDestroy() {
		this.subs.forEach((item) => item.unsubscribe());
	}

	initModal() {
		this.subs.push(
			this.modalService.isModalOpen$.subscribe({
				next: (res) => {
					if (!res) {
						this.isOpenDeleteNotification = false;
						this.isOpenDeleteAllNotification = false;
					}
				},
			})
		);
	}

	initFilter() {
		this.subs.push(
			this.notificationDataService.loadNotificationFilter().subscribe({
				next: (res) => {
					this.filterStatuses = res["is_notification_read"];
					this.priorityStatuses = res["priority"];
					this.relationStatuses = res["notification_relation"];
					this.filterAdditional = res["additional_params"];
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterSetData$.subscribe({
				next: (res) => {
					this.filterData = res;
					this.currentPage = 0;

					this.selectedNotification = null;
					this.notificationList = [];
					this.loadNotification();
				},
			})
		);

		this.subs.push(
			this.filterSettingService.inputSearch$.pipe(debounceTime(300)).subscribe({
				next: (res) => {
					this.notificationDataService.loadSearchList(res).subscribe({
						next: (res) => {
							this.filterSettingService.responseSearchData$.next(res.content);
						},
					});
				},
			})
		);
		this.subs.push(
			this.filterSettingService.filterSearchWord$.subscribe({
				next: (res) => {
					this.searchWord = res;
				},
			})
		);

		this.subs.push(
			this.filterSettingService.filterAllReset$.subscribe({
				next: () => {
					this.searchWord = "";
					this.filterData = undefined;
					this.currentPage = 0;

					this.notificationList = [];
					this.loadNotification();
				},
			})
		);
	}

	loadNotification() {
		this.lastPage = true;

		this.notificationDataService.loadUserNotificationList(this.currentPage, this.searchWord, this.filterData).subscribe({
			next: (res) => {
				this.notificationList.push(...res.content);
				this.currentPage++;
				this.lastPage = res.last;
			},
			complete: () => {
				const dateArr = this.notificationList.map((el) => el.creation_date.split(" ")[0]).map((el) => el.split("-")[1]);

				for (let i = 0; i < dateArr.length; i++) {
					if (i === 0 || (dateArr[i] !== dateArr[i - 1] && i !== dateArr.length - 1)) {
						this.notificationList[i]["month"] = parseInt(this.notificationList[i].creation_date.split(/[- :]/)[1]) - 1;
					}
				}
			},
		});
	}

	@HostListener("document:scroll", ["$event"])
	onWindowScroll(event: any): void {
		if (window.innerWidth <= 768) {
			if (this.tableBlock?.nativeElement.clientHeight - window.scrollY - window.innerHeight + 120 < this.delta && !this.lastPage) {
				this.loadNotification();
			}
		}
	}

	toggleSort() {
		this.sortState = !this.sortState;

		this.notificationDataService
			.loadUserNotificationList(NULL_PAGE, this.searchWord, this.filterData, this.sortState ? "DESC" : "ASC")
			.pipe(take(1))
			.subscribe({
				next: (res) => {
					this.notificationList = res.content;
					this.lastPage = res.last;
				},
			});
	}

	toggleFilter(filterState: boolean) {
		this.isOpenFilter = filterState;
	}

	selectTab(itemTab: string) {
		this.tabSelect = itemTab;
		this.lastPage = true;

		if (this.tabSelect === "all") {
			this.filterData = {
				...this.filterData,
				notification_relation: null,
			};
		}
		if (this.tabSelect === "for-me") {
			this.filterData = {
				...this.filterData,
				notification_relation: "PERSONAL",
			};
		}
		if (this.tabSelect === "common") {
			this.filterData = {
				...this.filterData,
				notification_relation: "GENERAL",
			};
		}

		this.notificationList = [];
		this.lastPage = false;
		this.currentPage = 0;

		this.notificationDataService.loadUserNotificationList(NULL_PAGE, this.searchWord, this.filterData).subscribe({
			next: (res) => {
				this.notificationList = res.content;
				this.lastPage = res.last;
			},
			complete: () => {
				const dateArr = this.notificationList.map((el) => el.creation_date.split(" ")[0]).map((el) => el.split("-")[1]);

				for (let i = 0; i < dateArr.length; i++) {
					if (i === 0 || (dateArr[i] !== dateArr[i - 1] && i !== dateArr.length - 1)) {
						this.notificationList[i]["month"] = parseInt(this.notificationList[i].creation_date.split(/[- :]/)[1]) - 1;
					}
				}
			},
		});
	}

	onScroll(event: any) {
		const tableBodyHeight = this.tableBody?.nativeElement.clientHeight;
		const tableBlockHeight = this.tableBlock?.nativeElement.clientHeight;
		const scrollTable = event.target.scrollTop;

		if (tableBodyHeight - tableBlockHeight - scrollTable < this.delta && !this.lastPage && scrollTable !== 0) {
			this.loadNotification();
		}
	}

	public readNotification(item: NotificationItemDto) {
		this.selectedNotification = item;
		this.notificationDataService.setReadStatus(item.id).subscribe({
			next: () => {
				const notification = this.notificationList.find((el) => el.id === item.id);
				notification!.is_notification_read = true;
				this.updateCountNotification();
			},
		});
	}

	readAllNotifications() {
		this.notificationDataService.readAllNotifications().subscribe({
			next: () => {
				this.currentPage = 0;
				this.notificationList = [];
				this.lastPage = false;
				this.notificationService.setConfiguration(
					"icon-2",
					"warning",
					"NOTIFICATION_PAGE.NOTIFICATION.READ_TITLE",
					"NOTIFICATION_PAGE.NOTIFICATION.DESCRIPTION"
				);
				this.notificationService.showNotification();
				this.loadNotification();
			},
		});
	}

	openDeleteAllDialog() {
		this.isOpenDeleteAllNotification = true;
	}

	openDeleteItemDialog(id: number) {
		this.notificationId = id;
		this.isOpenDeleteNotification = true;
	}

	deleteItemNotification(id: number) {
		this.notificationDataService.deleteItemNotification(id).subscribe({
			next: () => {
				for (let i = 0; i < this.notificationList.length; i++) {
					if (this.notificationList[i].id === id) {
						this.notificationList.splice(i, 1);
						this.modalService.closeModal();
						this.notificationService.setConfiguration(
							"icon-6",
							"error",
							"NOTIFICATION_PAGE.NOTIFICATION.DELETE_TITLE",
							"NOTIFICATION_PAGE.NOTIFICATION.DESCRIPTION"
						);
						this.notificationService.showNotification();
						this.updateCountNotification();
						this.selectedNotification = null;
						return;
					}
				}
			},
		});
	}

	deleteAllNotification() {
		this.notificationDataService.deleteAllNotification().subscribe({
			next: () => {
				this.notificationList = [];
				this.modalService.closeModal();
				this.notificationService.setConfiguration(
					"icon-6",
					"error",
					"NOTIFICATION_PAGE.NOTIFICATION.DELETE_TITLE",
					"NOTIFICATION_PAGE.NOTIFICATION.DESCRIPTION"
				);
				this.notificationService.showNotification();
				this.updateCountNotification();
				this.selectedNotification = null;
				return;
			},
		});
	}

	updateCountNotification() {
		this.notificationDataService.loadCount().subscribe({
			next: (res) => {
				this.userInfoService.userNotificationCount$.next(res.unread_count);
			},
		});
	}
}
